<template>
  <z-select
    v-if="isLovColumn"
    ref="controlRef"
    :model-value="lovModel"
    :placeholder="column.title"
    :size="size"
    :clearable="clearable"
    :loading="lovLoading"
    teleported
    :class="inputClass"
    @update:model-value="onLovChange"
  >
    <z-option
      v-for="item in lovOptions"
      :key="String(item.value)"
      :label="item.label"
      :value="String(item.value)"
      :disabled="item.disabled"
    />
  </z-select>

  <z-input-table
    v-else-if="isTableColumn"
    ref="controlRef"
    :model-value="tableModel"
    :source="tableSource"
    :map="tableMap"
    :options="tableOptions"
    :display-text="tableDisplayText"
    :placeholder="column.title"
    :size="size"
    :clearable="clearable"
    :class="inputClass"
    @update:model-value="onTableChange"
  />

  <z-input-number
    v-else-if="column.type === 'number'"
    ref="controlRef"
    :model-value="numberModel"
    :size="size"
    :class="inputClass"
    controls-position="right"
    @update:model-value="onNumberChange"
  />

  <z-date-picker
    v-else-if="column.type === 'date' || column.type === 'datetime'"
    ref="controlRef"
    :model-value="dateModel"
    :type="column.type === 'datetime' ? 'datetime' : 'date'"
    :value-format="
      column.type === 'datetime' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
    "
    :size="size"
    :clearable="clearable"
    :class="inputClass"
    @update:model-value="onDateChange"
  />

  <z-input
    v-else
    ref="controlRef"
    :model-value="textModel"
    :size="size"
    :clearable="clearable"
    :class="inputClass"
    @update:model-value="onTextChange"
  />
</template>

<script setup lang="ts">
import {
  computed,
  reactive,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import ZInput from '@zoryn/components/input'
import ZInputNumber from '@zoryn/components/input-number'
import ZDatePicker from '@zoryn/components/date-picker'
import ZInputTable from '@zoryn/components/input-table'
import ZSelect, { ZOption } from '@zoryn/components/select'
import {
  buildLovCacheKey,
  getLovFromCache,
  useLov,
  useLovCacheVersion,
} from '@zoryn/hooks'
import {
  resolveLovDisplayField,
  resolveTableInputTableMap,
  resolveTableInputTableOptions,
} from '../utils/input-table-picker'
import type { GridColumnInfo } from '../types'
import type { ComponentSize } from '@zoryn/constants'

const DEFAULT_CACHE_TTL = 5 * 60 * 1000

const props = withDefaults(
  defineProps<{
    column: GridColumnInfo
    size?: ComponentSize
    clearable?: boolean
    inputClass?: string
  }>(),
  {
    size: 'default',
    clearable: true,
    inputClass: '',
  }
)

const model = defineModel<unknown>({ default: null })

type FocusableControl = ComponentPublicInstance & { focus?: () => void }

const controlRef = ref<FocusableControl>()

function focus() {
  controlRef.value?.focus?.()
}

defineExpose({ focus })

const lovConfig = useGlobalConfig('lov')
const lovCacheVersion = useLovCacheVersion()

const isLovColumn = computed(
  () => props.column.type === 'lov' && !!props.column.lovType
)
const isTableColumn = computed(() => props.column.type === 'table')

const syncLovOptions = computed(() => {
  void lovCacheVersion.value
  const type = props.column.lovType
  if (!type) {
    return []
  }
  const ttl =
    lovConfig.value?.cacheTtl === undefined
      ? DEFAULT_CACHE_TTL
      : lovConfig.value.cacheTtl
  if (ttl <= 0) {
    return []
  }
  return getLovFromCache(buildLovCacheKey(type), ttl) ?? []
})

const {
  options: lovOptions,
  loading: lovLoading,
  findOption,
} = useLov(() => props.column.lovType, { staticOptions: syncLovOptions })

const lovModel = computed(() => {
  const val = model.value
  if (val == null || val === '') {
    return ''
  }
  return String(val)
})

function onLovChange(value: unknown) {
  if (value == null || value === '') {
    model.value = null
    return
  }
  const matched = findOption(value)
  model.value = matched?.value ?? value
}

const tableSource = reactive<Record<string, unknown>>({})
const tableMap = computed(() => resolveTableInputTableMap(props.column))
const tableOptions = computed(() => resolveTableInputTableOptions(props.column))

watch(
  () => [props.column.field, model.value] as const,
  ([field, value]) => {
    if (!field) {
      return
    }
    tableSource[field] = value ?? null
  },
  { immediate: true }
)

const tableDisplayText = computed(() => {
  const displayField = resolveLovDisplayField(props.column)
  if (
    displayField &&
    tableSource[displayField] != null &&
    tableSource[displayField] !== ''
  ) {
    return String(tableSource[displayField])
  }
  if (model.value == null || model.value === '') {
    return ''
  }
  return String(model.value)
})

const tableModel = computed((): string | number | null => {
  const val = model.value
  if (val == null || val === '') {
    return null
  }
  if (typeof val === 'number') {
    return val
  }
  return String(val)
})

function onTableChange(value: string | number | null | undefined) {
  model.value = value == null || value === '' ? null : value
}

const textModel = computed(() => {
  const val = model.value
  return val == null ? '' : String(val)
})

function onTextChange(value: string) {
  model.value = value === '' ? null : value
}

const numberModel = computed((): number | undefined => {
  const val = model.value
  if (val == null || val === '') {
    return undefined
  }
  const num = Number(val)
  return Number.isNaN(num) ? undefined : num
})

function onNumberChange(value: number | undefined) {
  model.value = value ?? null
}

const dateModel = computed(
  () => model.value as string | number | Date | null | undefined
)

function onDateChange(value: string | number | Date | null | undefined) {
  model.value = value ?? null
}
</script>
